



























































































































































































import axios from 'axios';
import gql from 'graphql-tag';
import { Component, Vue } from 'vue-property-decorator';

import { ratesEndpoints } from '@/utils/config';
import { DuplicateNameGroup, findDuplicateNameGroups } from '@/utils/workTypeName';
import {
  RatesFilter,
  parseRatesFilter,
  ratesPageQuery,
  serializeRatesFilter,
} from '@/utils/ratesFilterQuery';

interface ConflictItem {
  work_type_id: number;
  work_type_name: string | null;
  severity: 'dead' | 'partial' | 'gap' | 'clean';
  counts: { dead: number; partial: number; gap: number };
  suppliers: Array<{ id: number; name: string | null }>;
  generic_affected: boolean;
  findings_total: number;
  platformId?: string;
  platformName?: string;
  clientId?: string;
  clientName?: string;
  supplierNames?: string;
}

const SEVERITY_LABELS: Record<string, string> = {
  dead: '🔴 цена не применяется',
  partial: '🟠 применяется частично',
  gap: '🔵 дырка в покрытии',
  clean: 'чисто',
};

const SEVERITY_COLORS: Record<string, string> = {
  dead: 'error',
  partial: 'warning',
  gap: 'info',
  clean: 'success',
};

/**
 * Список видов работ, где полосы тарифа перекрываются (задача 10577). Строка — вид работ,
 * класс по худшему замечанию: так «5 и 2» читается как «7 позиций разобрать» и совпадает
 * с числом строк. Считает всё бэкенд (`GET /tariff-conflicts`), тем же правилом, что и
 * расчёт; страница только показывает и ведёт в `/rates` с готовым фильтром.
 *
 * Площадку и клиента эндпоинт не знает — их держит Pimcore, поэтому они дозапрашиваются
 * по возвращённым id одним GraphQL-запросом. Не ответил Pimcore — строки останутся без
 * площадки, а ссылка поведёт по одному виду работ: список от этого не ломается.
 */
@Component
export default class RatesConflictsPage extends Vue {
  loading = false;

  error = '';

  counters: Record<string, any> | null = null;

  rows: Array<ConflictItem> = [];

  severity = '';

  workTypeId = '';

  supplierId = '';

  duplicateGroups: Array<DuplicateNameGroup> = [];

  duplicatesLoading = false;

  duplicatesError = '';

  duplicateHeaders = [
    { text: 'Название', value: 'name', sortable: false },
    { text: 'Площадка / клиент', value: 'place', sortable: false },
    { text: 'Виды работ (id)', value: 'ids', sortable: false },
    { text: '', value: 'actions', sortable: false },
  ];

  noPlaceHint = 'Площадка и клиент этого вида работ не найдены в справочнике — '
    + 'открыть тариф по ссылке нельзя, выберите площадку на экране тарифов вручную.';

  severityItems = [
    { text: '🔴 цена не применяется', value: 'dead' },
    { text: '🟠 применяется частично', value: 'partial' },
    { text: '🔵 дырка в покрытии', value: 'gap' },
  ];

  headers = [
    { text: 'Класс', value: 'severity', sortable: false },
    { text: 'Вид работ', value: 'workType', sortable: false },
    { text: 'Площадка / клиент', value: 'place', sortable: false },
    { text: 'У кого цена не применяется', value: 'suppliers', sortable: false },
    { text: '🔴 / 🟠 / 🔵', value: 'counts', sortable: false },
    { text: '', value: 'actions', sortable: false },
  ];

  created() {
    const filter = parseRatesFilter(this.$route.query);
    this.severity = filter.severity;
    this.workTypeId = filter.workType;
    this.supplierId = filter.supplier;
    this.load(); this.loadDuplicates();
  }

  duplicateRatesLink(group: DuplicateNameGroup) {
    return {
      name: 'RatesPage',
      query: ratesPageQuery({ platform: group.platformId, client: group.clientId }),
    };
  }

  /**
   * Дубли названий считает фронт по справочнику Pimcore (задача 13295): у `/tariff-conflicts`
   * нет площадки и клиента. Листинг без `published` отдаёт только опубликованные — снятые
   * с публикации у FM архив и дублем не считаются.
   */
  async loadDuplicates() {
    this.duplicatesLoading = true;
    this.duplicatesError = '';
    try {
      const resp = await this.$apollo.query({
        query: gql`{
          getWorkListing(first: 100000) {
            edges {
              node {
                id
                name
                platform { ... on object_PlantsHR { id name } }
                client { ... on object_Client { id legal_name } }
              }
            }
          }
        }`,
        fetchPolicy: 'network-only',
      });
      const edges = resp?.data?.getWorkListing?.edges || [];
      this.duplicateGroups = findDuplicateNameGroups(edges.map((edge: any) => edge.node));
    } catch (e) {
      this.duplicateGroups = [];
      this.duplicatesError = `Не удалось проверить названия видов работ: ${(e as Error).message}`;
    }
    this.duplicatesLoading = false;
  }

  get filter(): Partial<RatesFilter> {
    return {
      severity: this.severity || '',
      workType: this.workTypeId || '',
      supplier: this.supplierId || '',
    };
  }

  applyFilter() {
    const query = serializeRatesFilter(this.filter, { ...this.$route.query });
    if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
      this.$router.replace({ query }).catch(() => {
        // Vue Router ругается на повторную навигацию — адрес уже совпадает.
      });
    }
    this.load();
  }

  severityLabel(severity: string): string {
    return SEVERITY_LABELS[severity] || severity;
  }

  severityColor(severity: string): string {
    return SEVERITY_COLORS[severity] || 'grey';
  }

  /**
   * Ссылка ведёт в `/rates` только когда известны площадка и клиент. Без них экран тарифов
   * подставляет запомненный в localStorage фильтр и открывается на **чужой** площадке, а
   * `workType` в списке не выбирается — переход выглядел бы рабочим и врал (проверено на
   * проде по виду работ 98567, которого нет в справочнике; задача 10577).
   */
  canOpenRates(item: ConflictItem): boolean {
    return !!(item.platformId && item.clientId);
  }

  ratesLink(item: ConflictItem) {
    return {
      name: 'RatesPage',
      query: ratesPageQuery({
        platform: item.platformId || '',
        client: item.clientId || '',
        workType: String(item.work_type_id),
      }),
    };
  }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const params = new URLSearchParams();
      if (this.severity) {
        params.append('severity', this.severity);
      }
      if (this.workTypeId) {
        params.append('work_type_id', String(this.workTypeId).trim());
      }
      if (this.supplierId) {
        params.append('supplier_id', String(this.supplierId).trim());
      }
      const resp = await axios.get(ratesEndpoints.conflicts, { params });
      const payload = resp?.data?.data || resp?.data || {};
      this.counters = payload.counters || null;
      this.rows = (payload.items || []).map((item: ConflictItem) => ({
        ...item,
        supplierNames: (item.suppliers || [])
          .map((supplier) => supplier.name || `Поставщик #${supplier.id}`)
          .join(', '),
      }));
      await this.enrichWithPimcore();
    } catch (e) {
      this.error = `Не удалось загрузить список перекрытий: ${(e as Error).message}`;
      this.rows = [];
      this.counters = null;
    }
    this.loading = false;
  }

  /** Площадка и клиент по id вида работ — одним запросом с алиасами, а не по строке на вид. */
  async enrichWithPimcore() {
    const ids = this.rows.map((row) => Number(row.work_type_id)).filter((id) => id > 0);
    if (!ids.length) {
      return;
    }
    const body = ids.map((id) => `w${id}: getWork(id: ${id}) {
      id
      platform { ... on object_PlantsHR { id name } }
      client { ... on object_Client { id legal_name } }
    }`).join('\n');

    try {
      const resp = await this.$apollo.query({
        query: gql`{ ${body} }`,
        fetchPolicy: 'network-only',
        // Часть видов работ в справочнике не заведена («object with ID N not found»), и
        // Pimcore отвечает `errors` вместе с данными по остальным. При политике по
        // умолчанию Apollo считает такой ответ ошибкой и площадка теряется у всех строк
        // сразу — а не только у ненайденной (задача 10577).
        errorPolicy: 'all',
      });
      const data = (resp?.data || {}) as Record<string, any>;
      this.rows = this.rows.map((row) => {
        const node = data[`w${row.work_type_id}`];
        if (!node) {
          return row;
        }
        return {
          ...row,
          platformId: node.platform?.id ? String(node.platform.id) : '',
          platformName: node.platform?.name || '',
          clientId: node.client?.id ? String(node.client.id) : '',
          clientName: node.client?.legal_name || '',
        };
      });
    } catch (e) {
      // Площадка и клиент — удобство, а не условие: без них ссылка ведёт по виду работ.
      // eslint-disable-next-line no-console
      console.warn('[RatesConflictsPage] Pimcore не отдал площадку и клиента', e);
    }
  }
}

